// hooks/playground/usePLCalculations.js
import { useMemo, useCallback } from 'react';

export const usePLCalculations = (organizedData, temporaryRows, months) => {
  const calculateGroupTotal = useCallback((items, monthNum, groupKey = null) => {
    let total = items.reduce((sum, item) => {
      return sum + (item.projected_amounts[monthNum] || 0);
    }, 0);

    if (groupKey && temporaryRows[groupKey]) {
      total += temporaryRows[groupKey].reduce((sum, tempRow) => {
        return sum + (tempRow.values[monthNum] || 0);
      }, 0);
    }

    return total;
  }, [temporaryRows]);

  const calculateQuarterlyTotal = useCallback((items, quarterMonths, groupKey = null) => {
    return quarterMonths.reduce((sum, month) => {
      return sum + calculateGroupTotal(items, month.month_num, groupKey);
    }, 0);
  }, [calculateGroupTotal]);

  // Income totals
  const incomeTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = calculateGroupTotal(organizedData.income, m);
      Object.keys(temporaryRows).forEach(key => {
        if (key.startsWith('income-')) {
          totals[m] += (temporaryRows[key] || []).reduce((sum, tempRow) => {
            return sum + (tempRow.values[m] || 0);
          }, 0);
        }
      });
    }
    return totals;
  }, [organizedData.income, temporaryRows, calculateGroupTotal]);

  // COGS totals
  const cogsTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = calculateGroupTotal(organizedData.cogs, m);
      Object.keys(temporaryRows).forEach(key => {
        if (key.startsWith('cogs-')) {
          totals[m] += (temporaryRows[key] || []).reduce((sum, tempRow) => {
            return sum + (tempRow.values[m] || 0);
          }, 0);
        }
      });
    }
    return totals;
  }, [organizedData.cogs, temporaryRows, calculateGroupTotal]);

  // Gross Profit
  const grossProfitTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = incomeTotals[m] - cogsTotals[m];
    }
    return totals;
  }, [incomeTotals, cogsTotals]);

  // Expenses totals
  const expensesTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = calculateGroupTotal(organizedData.expenses, m);
      Object.keys(temporaryRows).forEach(key => {
        if (key.startsWith('expenses-')) {
          totals[m] += (temporaryRows[key] || []).reduce((sum, tempRow) => {
            return sum + (tempRow.values[m] || 0);
          }, 0);
        }
      });
    }
    return totals;
  }, [organizedData.expenses, temporaryRows, calculateGroupTotal]);

  // Net Operating Income
  const netOperatingIncomeTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = grossProfitTotals[m] - expensesTotals[m];
    }
    return totals;
  }, [grossProfitTotals, expensesTotals]);

  // Other Income
  const otherIncomeTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = calculateGroupTotal(organizedData.otherIncome, m);
    }
    return totals;
  }, [organizedData.otherIncome, calculateGroupTotal]);

  // Other Expenses
  const otherExpensesTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = calculateGroupTotal(organizedData.otherExpenses, m);
    }
    return totals;
  }, [organizedData.otherExpenses, calculateGroupTotal]);

  // Net Other Income
  const netOtherIncomeTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = otherIncomeTotals[m] - otherExpensesTotals[m];
    }
    return totals;
  }, [otherIncomeTotals, otherExpensesTotals]);

  // Net Income
  const netIncomeTotals = useMemo(() => {
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = netOperatingIncomeTotals[m] + netOtherIncomeTotals[m];
    }
    return totals;
  }, [netOperatingIncomeTotals, netOtherIncomeTotals]);

  // Labor Costs
  const laborCostsTotals = useMemo(() => {
    const totals = {};
    const laborItems = organizedData.expenses.filter(item => {
      const subType = (item.account_sub_type || '').toLowerCase();
      const accountName = (item.account_name || '').toLowerCase();
      const fqn = (item.fully_qualified_name || '').toLowerCase();
      return (
        subType.includes('payroll') ||
        fqn.includes('payroll') ||
        fqn.includes('labor') ||
        accountName.includes('salaries') ||
        accountName.includes('wages') ||
        accountName.includes('401k') ||
        accountName.includes('bonuses')
      );
    });

    for (let m = 1; m <= 12; m++) {
      totals[m] = calculateGroupTotal(laborItems, m);
    }
    return totals;
  }, [organizedData.expenses, calculateGroupTotal]);

  // AMI Breakdown
  const amiBreakdown = useMemo(() => {
    const breakdown = {};
    
    for (let m = 1; m <= 12; m++) {
      const income = incomeTotals[m] || 0;
      const grossProfit = grossProfitTotals[m] || 0;
      const laborCosts = laborCostsTotals[m] || 0;
      const expenses = expensesTotals[m] || 0;
      const overhead = expenses - laborCosts;
      const netOperatingIncome = netOperatingIncomeTotals[m] || 0;
      const netIncome = netIncomeTotals[m] || 0;
      
      breakdown[m] = {
        laborCostsPct: grossProfit > 0 ? (laborCosts / grossProfit) * 100 : 0,
        overheadPct: grossProfit > 0 ? (overhead / grossProfit) * 100 : 0,
        profitPct: grossProfit > 0 ? (netOperatingIncome / grossProfit) * 100 : 0,
        netIncomePct: income > 0 ? (netIncome / income) * 100 : 0,
      };
    }
    
    return breakdown;
  }, [incomeTotals, grossProfitTotals, laborCostsTotals, expensesTotals, netOperatingIncomeTotals, netIncomeTotals]);

  return {
    calculateGroupTotal,
    calculateQuarterlyTotal,
    incomeTotals,
    cogsTotals,
    grossProfitTotals,
    expensesTotals,
    netOperatingIncomeTotals,
    otherIncomeTotals,
    otherExpensesTotals,
    netOtherIncomeTotals,
    netIncomeTotals,
    laborCostsTotals,
    amiBreakdown,
  };
};